import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { AvailableActions, PublishedApp, PublishedAppsResult, Recipe, RecipeResponse, WorkflowListType } from '../../workfloweditor/workflow-types';
import { useApim, useApimPost } from '../../hooks/useApi';
import { GoFormzServices } from '../../AppSettings';
import { getRecipeCmsQuery } from '../../workflowselect/helpers';
import { useAvailableActions } from '../../workfloweditor/StepMetadata';
import { getRecipesPerEnvironment } from './helpers';

interface WorkflowUpgradeContextProps {
  selectedAppIndex: number;
  setSelectedAppIndex: (index: number) => void;
  orderedPublishedApps: PublishedApp[];
  availableActions: AvailableActions;
  recipes: Recipe[];
  recipesLoading: boolean;
  recipesLoaded: boolean;
  appsLoaded: boolean;
  trialRecipes: Recipe[];
  workflowsCount: number;
  workflowsLoaded: boolean;
  refreshWorkflowCountIfZero: () => void;
  workflowLoading: boolean;
}

const WorkflowContext = createContext<WorkflowUpgradeContextProps | undefined>(undefined);

export const WorkflowRecipeProvider = ({ children }: { children: ReactNode }) => {
  const [selectedAppIndex, setSelectedAppIndex] = useState(0);
  const [orderedPublishedApps, setPublishedApps] = useState<PublishedApp[]>([]);
  const [appsResults, , appsLoaded] = useApim<PublishedAppsResult>(GoFormzServices.ApplicationService, '/publishedapps?onlyActive=true');
  const [recipeResponse, recipesLoading, recipesLoaded] = useApimPost<RecipeResponse>(GoFormzServices.CmsService, '/cms/graphql', getRecipeCmsQuery());
  const [availableActions] = useAvailableActions(orderedPublishedApps);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [workflowsResult, workflowLoading, workflowsLoaded, refreshWorkflow] = useApim<WorkflowListType>(GoFormzServices.WorkflowService, `/workflows?take=1`);
  const workflowsCount = workflowsResult?.data?.count ?? null;

  const recipes = recipesLoaded && getRecipesPerEnvironment(recipeResponse);
  const trialRecipes = recipesLoaded && recipes?.filter((x) => x.trial);

  const refreshWorkflowCountIfZero = () => {
    // isInitialLoad prevents the API from being called twice during the initial load, which can be triggered by a location change.
    if (workflowsCount === 0 && !workflowLoading && !isInitialLoad && appsLoaded) {
      refreshWorkflow();
    }
  };

  useEffect(() => {
    if (appsLoaded) {
      const appsInOrder: PublishedApp[] = [];
      for (const appId of appOrder) {
        const app = appsResults.data.items.find((x) => x.appId === appId);
        if (app) {
          appsInOrder.push(app);
        }
      }
      setPublishedApps(appsInOrder);
      setIsInitialLoad(false);
    }
  }, [appsLoaded, appsResults]);

  return (
    <WorkflowContext.Provider
      value={{
        selectedAppIndex,
        setSelectedAppIndex,
        orderedPublishedApps,
        availableActions,
        recipes,
        recipesLoaded,
        recipesLoading,
        appsLoaded,
        trialRecipes,
        workflowsCount,
        workflowsLoaded,
        refreshWorkflowCountIfZero,
        workflowLoading
      }}
    >
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflowContext = () => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflowContext must be used within a WorkflowRecipeProvider');
  }
  return context;
};

const appOrder = [
  '4723e786-7c8d-4229-ad0a-2638259664c8',
  '8666ba3b-dea2-457e-a429-7c0abf628533',
  '1f22832b-a503-427b-996c-e94d8d5f5bdb',
  '4a204cd9-488f-47f6-91c8-4517eadf246c',
  '9522abf4-caf6-499d-ac4e-68df695613e3',
  '4cec1a95-00fc-4975-9e0b-2231fb6fcb8f',
  '0752b501-9b5b-4be5-b3a5-5213ea90e299',
  'f93fb632-4d0c-4c7e-ac2a-ac6c1822b06e',
  '29f48a9e-75db-44d2-bd0b-58e236aaa8a0',
  'cc4396f0-9f3f-45b0-a13e-50be4bd76706',
  '64672e04-c4ff-49e8-87ec-464673d1055e',
  'a061235f-0f73-4778-bfd6-7daba284ed2e',
  '695cea5e-bef0-4d57-9bd4-5c3eb2bb7a64',
  '1118090d-b563-4957-a882-2a411748bc33',
  '549e1ad3-3ceb-49ad-8bb0-d53f76c611e1',
  '46309c96-6744-4204-8877-2883acec83b5',
  'f88c7d87-70f3-494d-ab43-13c51bc97f93',
  '35056c45-dbab-4fa7-8af7-c2cedc4f2705',
  '9a487f31-62c8-40bc-867a-607e743d4712',
  '61135784-9b10-4988-a637-e5ac98a94044',
  '24bdb6f4-3aea-49eb-be4e-73c5c335bcec',
  '2acb03c3-f7f0-4643-ad98-358b1e32ef53'
];
